import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import API_BASE_URL from '../config/api';

interface Message {
  id: string;
  role: 'USER' | 'ASSISTANT' | 'SYSTEM';
  content: string;
  tokens?: number;
  createdAt: string;
}

interface ChatSession {
  id: string;
  sessionId: string;
  title: string;
  isActive: boolean;
  createdAt: string;
}

interface UsageWarning {
  type: 'warning' | 'critical' | 'exceeded';
  service: string;
  used: number;
  limit: number;
  percentage: number;
}

const Chat: React.FC = () => {
  const navigate = useNavigate();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentSession, setCurrentSession] = useState<ChatSession | null>(null);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [error, setError] = useState('');
  const [usageWarning, setUsageWarning] = useState<UsageWarning | null>(null);
  const [planType, setPlanType] = useState<string>('FREE');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    loadChatSessions();
    checkUsageLimits();
  }, []);

  // Auto scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Check usage limits
  const checkUsageLimits = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/api/usage/current`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        setPlanType(data.data.subscription.planType);
        
        // Check for usage warnings
        const services = data.data.services;
        for (const [serviceName, service] of Object.entries(services)) {
          const serviceData = service as any;
          if (serviceData.limit && serviceData.percentageUsed > 75) {
            setUsageWarning({
              type: serviceData.percentageUsed > 90 ? 'critical' : 'warning',
              service: serviceName.replace('_', ' ').toLowerCase(),
              used: serviceData.used,
              limit: serviceData.limit,
              percentage: serviceData.percentageUsed
            });
            break;
          }
        }
      }
    } catch (error) {
      console.error('Failed to check usage limits:', error);
    }
  };

  // Load user's chat sessions
  const loadChatSessions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/chat/sessions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        setSessions(data.data);
        // Auto-select first session or create new one
        if (data.data.length > 0) {
          setCurrentSession(data.data[0]);
          loadChatHistory(data.data[0].sessionId);
        }
      }
    } catch (error) {
      console.error('Failed to load sessions:', error);
    }
  };

  // Start new chat session
  const startNewChat = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/chat/sessions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: `Chat ${new Date().toLocaleTimeString()}`
        })
      });

      const data = await response.json();
      if (data.success) {
        setCurrentSession(data.chatSession);
        setMessages([]);
        setSessions(prev => [data.chatSession, ...prev]);
        // Refresh usage after creating session
        checkUsageLimits();
      } else {
        // Handle usage limit errors
        if (data.code === 'USAGE_LIMIT_EXCEEDED') {
          setError(`Usage limit exceeded: ${data.message}`);
          setUsageWarning({
            type: 'exceeded',
            service: 'API requests',
            used: data.details?.used || 0,
            limit: data.details?.limit || 0,
            percentage: 100
          });
        } else {
          setError('Failed to start new chat');
        }
      }
    } catch (error) {
      setError('Failed to start new chat');
    } finally {
      setLoading(false);
    }
  };

  // Load chat history
  const loadChatHistory = async (sessionId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/chat/sessions/${sessionId}/history`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        setMessages(data.data.messages);
      }
    } catch (error) {
      console.error('Failed to load chat history:', error);
    }
  };

  // Send message
  const sendMessage = async () => {
    if (!inputMessage.trim() || !currentSession || loading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setLoading(true);
    setError('');

    // Add user message to UI immediately
    const tempUserMessage: Message = {
      id: `temp-${Date.now()}`,
      role: 'USER',
      content: userMessage,
      createdAt: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMessage]);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/chat/message`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionId: currentSession.sessionId,
          message: userMessage,
          systemPrompt: "You are a helpful AI assistant. Be concise and friendly."
        })
      });

      const data = await response.json();
      
      if (data.success) {
        // Replace temp message with real messages
        setMessages(prev => [
          ...prev.slice(0, -1), // Remove temp message
          data.data.userMessage,
          data.data.aiResponse
        ]);
        // Refresh usage after successful message
        checkUsageLimits();
      } else {
        // Handle usage limit errors
        if (data.code === 'USAGE_LIMIT_EXCEEDED') {
          setError(`Usage limit exceeded: ${data.message}`);
          setUsageWarning({
            type: 'exceeded',
            service: data.details?.metricType?.replace('_', ' ').toLowerCase() || 'usage',
            used: data.details?.used || 0,
            limit: data.details?.limit || 0,
            percentage: 100
          });
        } else {
          setError(data.message || 'Failed to send message');
        }
        // Remove temp message on error
        setMessages(prev => prev.slice(0, -1));
      }
    } catch (error) {
      console.error('Network/Parse error:', error);
      setError('Network error. Please try again.');
      // Remove temp message on error
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  // Handle Enter key press
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Logout function
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const getWarningColor = (type: string) => {
    switch (type) {
      case 'warning': return 'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-300 text-yellow-900';
      case 'critical': return 'bg-gradient-to-r from-orange-50 to-red-50 border-orange-300 text-orange-900';
      case 'exceeded': return 'bg-gradient-to-r from-red-50 to-pink-50 border-red-300 text-red-900';
      default: return 'bg-gradient-to-r from-gray-50 to-slate-50 border-gray-300 text-gray-900';
    }
  };

  const getPlanBadgeColor = (planType: string): string => {
    switch (planType?.toLowerCase()) {
      case 'premium': return 'bg-gradient-to-r from-purple-500 to-purple-700 text-white';
      case 'pro': return 'bg-gradient-to-r from-blue-500 to-blue-700 text-white';
      case 'basic': return 'bg-gradient-to-r from-green-500 to-green-700 text-white';
      default: return 'bg-gradient-to-r from-gray-500 to-gray-700 text-white';
    }
  };

  return (
    <div className="flex h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Enhanced Sidebar */}
      <div className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'} transition-transform duration-300 fixed lg:relative z-30 w-80 bg-white/80 backdrop-blur-lg border-r border-white/20 flex flex-col shadow-xl`}>
        {/* Sidebar Header */}
        <div className="p-6 bg-gradient-to-r from-indigo-500 to-purple-600">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <span className="text-white text-xl">💬</span>
              </div>
              <div>
                <h2 className="text-white font-bold text-lg">AI Chat</h2>
                <p className="text-indigo-100 text-sm">Intelligent Assistant</p>
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-white/80 hover:text-white"
            >
              ✕
            </button>
          </div>
          
          <button
            onClick={startNewChat}
            disabled={usageWarning?.type === 'exceeded'}
            className="w-full bg-white/20 backdrop-blur text-white rounded-xl px-4 py-3 hover:bg-white/30 transition-all duration-200 disabled:bg-gray-400/50 disabled:cursor-not-allowed font-medium shadow-lg hover:shadow-xl"
          >
            <span className="mr-2">✨</span>
            New Chat
          </button>
          {usageWarning?.type === 'exceeded' && (
            <p className="text-xs text-red-200 mt-2 text-center">
              Usage limit reached
            </p>
          )}
        </div>
        
        {/* Enhanced Plan Info */}
        <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                <span className="text-white text-xs">💎</span>
              </div>
              <span className="text-sm font-medium text-gray-700">Current Plan</span>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getPlanBadgeColor(planType)}`}>
              {planType}
            </span>
          </div>
          <Link
            to="/usage"
            className="inline-flex items-center text-xs text-blue-600 hover:text-blue-700 mt-2 font-medium"
          >
            <span className="mr-1">📊</span>
            View Usage Analytics →
          </Link>
        </div>
        
        {/* Enhanced Chat History */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="flex items-center mb-4">
            <div className="w-6 h-6 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center mr-2">
              <span className="text-white text-xs">📚</span>
            </div>
            <h3 className="font-semibold text-gray-800">Chat History</h3>
          </div>
          
          <div className="space-y-2">
            {sessions.map((session) => (
              <div
                key={session.id}
                onClick={() => {
                  setCurrentSession(session);
                  loadChatHistory(session.sessionId);
                  setSidebarOpen(false);
                }}
                className={`group p-4 rounded-xl cursor-pointer transition-all duration-200 ${
                  currentSession?.id === session.id
                    ? 'bg-gradient-to-r from-indigo-100 to-purple-100 border-2 border-indigo-200 shadow-md'
                    : 'bg-white/60 hover:bg-white hover:shadow-md border border-gray-100'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-gray-800 truncate group-hover:text-indigo-600">
                      {session.title}
                    </div>
                    <div className="flex items-center mt-1">
                      <span className="text-xs text-gray-500">
                        {new Date(session.createdAt).toLocaleDateString()}
                      </span>
                      <span className="mx-1 text-gray-300">•</span>
                      <span className="text-xs text-gray-500">
                        {new Date(session.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </span>
                    </div>
                  </div>
                  {currentSession?.id === session.id && (
                    <div className="w-2 h-2 bg-indigo-500 rounded-full ml-2"></div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Enhanced Navigation */}
        <div className="p-4 border-t border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <Link
            to="/"
            className="flex items-center text-indigo-600 hover:text-indigo-700 text-sm mb-3 p-2 rounded-lg hover:bg-indigo-50 transition-colors font-medium"
          >
            <span className="mr-2">🏠</span>
            Dashboard
          </Link>
          <button
            onClick={handleLogout}
            className="flex items-center w-full text-red-600 hover:text-red-700 text-sm p-2 rounded-lg hover:bg-red-50 transition-colors font-medium"
          >
            <span className="mr-2">🚪</span>
            Logout
          </button>
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Enhanced Header */}
        <div className="bg-white/80 backdrop-blur-lg border-b border-white/20 p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                <span className="text-gray-600">☰</span>
              </button>
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  {currentSession ? currentSession.title : 'AI Chat Assistant'}
                </h1>
                {currentSession && (
                  <p className="text-sm text-gray-500">
                    Session started {new Date(currentSession.createdAt).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2 bg-white/60 rounded-full px-3 py-1 border border-gray-200">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-xs font-medium text-gray-600">Online</span>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Usage Warning */}
        {usageWarning && (
          <div className={`mx-6 mt-4 p-4 rounded-2xl border-2 shadow-lg ${getWarningColor(usageWarning.type)}`}>
            <div className="flex justify-between items-start">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-white/80 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-lg">
                    {usageWarning.type === 'exceeded' ? '🚫' : 
                     usageWarning.type === 'critical' ? '⚠️' : '⚠️'}
                  </span>
                </div>
                <div>
                  <p className="font-bold text-lg">
                    {usageWarning.type === 'exceeded' ? 'Usage Limit Exceeded' : 
                     usageWarning.type === 'critical' ? 'Usage Limit Critical' : 'Usage Warning'}
                  </p>
                  <p className="text-sm mt-1 opacity-90">
                    <span className="font-semibold">{usageWarning.service}:</span> {usageWarning.used.toLocaleString()} / {usageWarning.limit.toLocaleString()} 
                    ({Math.round(usageWarning.percentage)}%)
                  </p>
                  {usageWarning.type === 'exceeded' && (
                    <p className="text-sm mt-2 font-medium">
                      Please upgrade your plan to continue using the service.
                    </p>
                  )}
                </div>
              </div>
              <div className="flex space-x-2 ml-4 flex-shrink-0">
                <Link
                  to="/usage"
                  className="px-3 py-1 bg-white/80 rounded-lg text-sm font-medium hover:bg-white transition-colors"
                >
                  View Usage
                </Link>
                <Link
                  to="/subscription"
                  className="px-3 py-1 bg-white/80 rounded-lg text-sm font-medium hover:bg-white transition-colors"
                >
                  Upgrade
                </Link>
                <button
                  onClick={() => setUsageWarning(null)}
                  className="px-2 py-1 bg-white/80 rounded-lg text-sm hover:bg-white transition-colors"
                >
                  ✕
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Enhanced Messages Area */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {messages.length === 0 ? (
            <div className="text-center text-gray-500 mt-16">
              <div className="w-20 h-20 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl">
                <span className="text-3xl">🤖</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Welcome to AI Chat!</h3>
              <p className="text-gray-600 mb-4">Start a conversation with your intelligent assistant</p>
              {planType && (
                <div className="inline-flex items-center space-x-2 bg-white/60 rounded-full px-4 py-2 border border-gray-200">
                  <span className="text-sm">Current plan:</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getPlanBadgeColor(planType)}`}>
                    {planType}
                  </span>
                </div>
              )}
            </div>
          ) : (
            messages.map((message, index) => (
              <div
                key={message.id}
                className={`flex ${message.role === 'USER' ? 'justify-end' : 'justify-start'} transition-all duration-500 ease-out`}
              >
                <div className="flex items-end space-x-3 max-w-4xl">
                  {message.role !== 'USER' && (
                    <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
                      <span className="text-white text-lg">🤖</span>
                    </div>
                  )}
                  <div
                    className={`px-6 py-4 rounded-2xl shadow-lg max-w-2xl ${
                      message.role === 'USER'
                        ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-br-md'
                        : 'bg-white/80 backdrop-blur border border-white/20 text-gray-800 rounded-bl-md'
                    }`}
                  >
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
                    <div className="flex items-center justify-between mt-3">
                      <span className={`text-xs ${message.role === 'USER' ? 'text-indigo-100' : 'text-gray-500'}`}>
                        {new Date(message.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </span>
                      {message.tokens && (
                        <span className={`text-xs ${message.role === 'USER' ? 'text-indigo-100' : 'text-gray-500'}`}>
                          {message.tokens} tokens
                        </span>
                      )}
                    </div>
                  </div>
                  {message.role === 'USER' && (
                    <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
                      <span className="text-white text-lg">👤</span>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
          
          {loading && (
            <div className="flex justify-start animate-pulse">
              <div className="flex items-end space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                  <span className="text-white text-lg">🤖</span>
                </div>
                <div className="bg-white/80 backdrop-blur border border-white/20 rounded-2xl rounded-bl-md px-6 py-4 shadow-lg">
                  <div className="flex space-x-2 items-center">
                    <div className="w-2 h-2 bg-indigo-500 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                    <div className="w-2 h-2 bg-indigo-500 rounded-full animate-bounce" style={{animationDelay: '0.4s'}}></div>
                    <span className="text-sm text-gray-600 ml-2">AI is thinking...</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Enhanced Error Message */}
        {error && (
          <div className="mx-6 mb-4 p-4 bg-gradient-to-r from-red-50 to-pink-50 border-2 border-red-200 text-red-800 rounded-2xl shadow-lg">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                  <span className="text-red-600">⚠️</span>
                </div>
                <span className="font-medium">{error}</span>
              </div>
              <button
                onClick={() => setError('')}
                className="p-1 hover:bg-red-100 rounded-full transition-colors"
              >
                <span className="text-red-600 text-lg">✕</span>
              </button>
            </div>
          </div>
        )}

        {/* Enhanced Input Area */}
        <div className="bg-white/80 backdrop-blur-lg border-t border-white/20 p-6">
          <div className="flex space-x-4 items-end">
            <div className="flex-1 relative">
              <textarea
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={
                  usageWarning?.type === 'exceeded' 
                    ? 'Usage limit exceeded. Please upgrade to continue.' 
                    : 'Type your message here... Press Enter to send'
                }
                className="w-full resize-none border-2 border-gray-200 rounded-2xl px-6 py-4 pr-12 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-100 transition-all duration-200 bg-white/80 backdrop-blur text-gray-800 placeholder-gray-500"
                rows={1}
                disabled={loading || !currentSession || usageWarning?.type === 'exceeded'}
                style={{
                  minHeight: '60px',
                  maxHeight: '120px'
                }}
              />
              <div className="absolute right-3 bottom-3 text-xs text-gray-400">
                {inputMessage.length > 0 && `${inputMessage.length} chars`}
              </div>
            </div>
            <button
              onClick={sendMessage}
              disabled={loading || !inputMessage.trim() || !currentSession || usageWarning?.type === 'exceeded'}
              className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white p-4 rounded-2xl hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:transform-none"
            >
              <span className="text-xl">🚀</span>
            </button>
          </div>
          {usageWarning?.type === 'exceeded' && (
            <div className="mt-4 text-center">
              <Link
                to="/subscription"
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-red-500 to-pink-600 text-white rounded-2xl hover:from-red-600 hover:to-pink-700 font-medium shadow-lg hover:shadow-xl transition-all duration-200"
              >
                <span className="mr-2">⚡</span>
                Upgrade your plan to continue chatting
              </Link>
            </div>
          )}
        </div>
      </div>


    </div>
  );
};

export default Chat;